'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Download, Webhook, Layers, Clock, CheckCircle, XCircle, AlertTriangle, RotateCcw, RefreshCw, ChevronDown, ChevronUp, Activity, Search, Play, Pause, Eye, Wifi, WifiOff, AlertCircle } from 'lucide-react';

import SectionHeader from '@/components/ui/SectionHeader';

// ─── Types ────────────────────────────────────────────────────────────────────

type JobStatus = 'running' | 'completed' | 'failed' | 'queued' | 'retrying' | 'paused';
type JobCategory = 'export' | 'webhook' | 'async';
type SLAStatus = 'on-track' | 'at-risk' | 'breached';

interface Job {
  id: string;
  name: string;
  category: JobCategory;
  status: JobStatus;
  priority: 'critical' | 'high' | 'normal' | 'low';
  startedAt: string | null;
  completedAt: string | null;
  scheduledAt: string;
  durationMs: number | null;
  slaDeadlineMs: number;
  slaStatus: SLAStatus;
  retryCount: number;
  maxRetries: number;
  nextRetryAt: string | null;
  progress: number | null;
  errorMessage: string | null;
  endpoint?: string;
  format?: string;
  recipients?: number;
  recordCount?: number;
  httpStatus?: number | null;
  throughput?: string;
}

interface SLARule {
  category: JobCategory;
  label: string;
  p50Ms: number;
  p95Ms: number;
  slaMs: number;
  currentP50: number;
  currentP95: number;
  breachCount: number;
  totalJobs: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const initialJobs: Job[] = [
  {
    id: 'EXP-20260821-0041',
    name: 'Quarterly FATCA/CRS Export',
    category: 'export',
    status: 'running',
    priority: 'critical',
    startedAt: '2026-08-21 03:38:12',
    completedAt: null,
    scheduledAt: '2026-08-21 03:30:00',
    durationMs: 250000,
    slaDeadlineMs: 600000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 3,
    nextRetryAt: null,
    progress: 67,
    errorMessage: null,
    format: 'XML',
    recipients: 4,
    recordCount: 12847,
    throughput: '1,284 rec/s',
  },
  {
    id: 'EXP-20260821-0040',
    name: 'Daily Investor Register (PDF)',
    category: 'export',
    status: 'completed',
    priority: 'high',
    startedAt: '2026-08-21 03:00:02',
    completedAt: '2026-08-21 03:04:18',
    scheduledAt: '2026-08-21 03:00:00',
    durationMs: 256000,
    slaDeadlineMs: 900000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 3,
    nextRetryAt: null,
    progress: 100,
    errorMessage: null,
    format: 'PDF',
    recipients: 6,
    recordCount: 7241,
    throughput: '472 rec/s',
  },
  {
    id: 'EXP-20260821-0039',
    name: 'AML Screening Batch Export',
    category: 'export',
    status: 'failed',
    priority: 'critical',
    startedAt: '2026-08-21 02:45:00',
    completedAt: '2026-08-21 02:46:33',
    scheduledAt: '2026-08-21 02:45:00',
    durationMs: 93000,
    slaDeadlineMs: 300000,
    slaStatus: 'breached',
    retryCount: 3,
    maxRetries: 3,
    nextRetryAt: null,
    progress: null,
    errorMessage: 'Database connection timeout after 90s — max retries exhausted',
    format: 'CSV',
    recipients: 3,
    recordCount: null,
    httpStatus: null,
  },
  {
    id: 'EXP-20260821-0038',
    name: 'Weekly KYC Summary (Excel)',
    category: 'export',
    status: 'queued',
    priority: 'normal',
    startedAt: null,
    completedAt: null,
    scheduledAt: '2026-08-21 04:00:00',
    durationMs: null,
    slaDeadlineMs: 1800000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 3,
    nextRetryAt: null,
    progress: null,
    errorMessage: null,
    format: 'Excel',
    recipients: 8,
    recordCount: null,
  },
  {
    id: 'WHK-20260821-0187',
    name: 'Settlement Confirmed → Regulator A',
    category: 'webhook',
    status: 'completed',
    priority: 'critical',
    startedAt: '2026-08-21 03:41:02',
    completedAt: '2026-08-21 03:41:02',
    scheduledAt: '2026-08-21 03:41:02',
    durationMs: 312,
    slaDeadlineMs: 5000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 5,
    nextRetryAt: null,
    progress: 100,
    errorMessage: null,
    endpoint: 'https://api.regulator-a.gov/events/inbound',
    httpStatus: 200,
  },
  {
    id: 'WHK-20260821-0186',
    name: 'KYC Approved → Custodian B',
    category: 'webhook',
    status: 'retrying',
    priority: 'high',
    startedAt: '2026-08-21 03:39:44',
    completedAt: null,
    scheduledAt: '2026-08-21 03:39:44',
    durationMs: null,
    slaDeadlineMs: 30000,
    slaStatus: 'at-risk',
    retryCount: 2,
    maxRetries: 5,
    nextRetryAt: '2026-08-21 03:42:14',
    progress: null,
    errorMessage: 'HTTP 503 — Service Unavailable',
    endpoint: 'https://hooks.custodian-b.com/kyc/events',
    httpStatus: 503,
  },
  {
    id: 'WHK-20260821-0185',
    name: 'AML Alert → Auditor Portal',
    category: 'webhook',
    status: 'failed',
    priority: 'critical',
    startedAt: '2026-08-21 03:35:18',
    completedAt: '2026-08-21 03:36:48',
    scheduledAt: '2026-08-21 03:35:18',
    durationMs: 90000,
    slaDeadlineMs: 10000,
    slaStatus: 'breached',
    retryCount: 5,
    maxRetries: 5,
    nextRetryAt: null,
    progress: null,
    errorMessage: 'HTTP 404 — Endpoint not found. Max retries exhausted.',
    endpoint: 'https://auditor.internal/webhooks/aml',
    httpStatus: 404,
  },
  {
    id: 'WHK-20260821-0184',
    name: 'Campaign Funded → Issuer Notify',
    category: 'webhook',
    status: 'completed',
    priority: 'normal',
    startedAt: '2026-08-21 03:28:55',
    completedAt: '2026-08-21 03:28:55',
    scheduledAt: '2026-08-21 03:28:55',
    durationMs: 198,
    slaDeadlineMs: 5000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 5,
    nextRetryAt: null,
    progress: 100,
    errorMessage: null,
    endpoint: 'https://api.issuer-platform.io/hooks/campaigns',
    httpStatus: 201,
  },
  {
    id: 'JOB-20260821-0092',
    name: 'Risk Score Recalculation Batch',
    category: 'async',
    status: 'running',
    priority: 'high',
    startedAt: '2026-08-21 03:40:00',
    completedAt: null,
    scheduledAt: '2026-08-21 03:40:00',
    durationMs: 120000,
    slaDeadlineMs: 600000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 2,
    nextRetryAt: null,
    progress: 44,
    errorMessage: null,
    recordCount: 3847,
    throughput: '32 rec/s',
  },
  {
    id: 'JOB-20260821-0091',
    name: 'Sanctions List Sync (OFAC/UN)',
    category: 'async',
    status: 'completed',
    priority: 'critical',
    startedAt: '2026-08-21 02:00:00',
    completedAt: '2026-08-21 02:03:41',
    scheduledAt: '2026-08-21 02:00:00',
    durationMs: 221000,
    slaDeadlineMs: 600000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 2,
    nextRetryAt: null,
    progress: 100,
    errorMessage: null,
    recordCount: 284712,
    throughput: '1,289 rec/s',
  },
  {
    id: 'JOB-20260821-0090',
    name: 'Distribution Calculation Run',
    category: 'async',
    status: 'queued',
    priority: 'high',
    startedAt: null,
    completedAt: null,
    scheduledAt: '2026-08-21 05:00:00',
    durationMs: null,
    slaDeadlineMs: 1200000,
    slaStatus: 'on-track',
    retryCount: 0,
    maxRetries: 2,
    nextRetryAt: null,
    progress: null,
    errorMessage: null,
    recordCount: null,
  },
  {
    id: 'JOB-20260821-0089',
    name: 'Ledger Reconciliation Sweep',
    category: 'async',
    status: 'retrying',
    priority: 'critical',
    startedAt: '2026-08-21 03:30:00',
    completedAt: null,
    scheduledAt: '2026-08-21 03:30:00',
    durationMs: null,
    slaDeadlineMs: 900000,
    slaStatus: 'at-risk',
    retryCount: 1,
    maxRetries: 2,
    nextRetryAt: '2026-08-21 03:45:00',
    progress: null,
    errorMessage: 'Deadlock detected on ledger_entries table — retry #1',
    recordCount: null,
  },
];

const slaRules: SLARule[] = [
  { category: 'export', label: 'Scheduled Exports', p50Ms: 180000, p95Ms: 420000, slaMs: 900000, currentP50: 256000, currentP95: 480000, breachCount: 1, totalJobs: 12 },
  { category: 'webhook', label: 'Webhook Deliveries', p50Ms: 280, p95Ms: 1200, slaMs: 5000, currentP50: 312, currentP95: 2100, breachCount: 2, totalJobs: 187 },
  { category: 'async', label: 'Async Job Queue', p50Ms: 95000, p95Ms: 280000, slaMs: 600000, currentP50: 221000, currentP95: 390000, breachCount: 0, totalJobs: 92 },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

function getSLAPercent(durationMs: number | null, slaMs: number): number {
  if (!durationMs) return 0;
  return Math.min(100, Math.round((durationMs / slaMs) * 100));
}

function getStatusConfig(status: JobStatus) {
  const map: Record<JobStatus, { label: string; color: string; bg: string; border: string; dot: string }> = {
    running:   { label: 'Running',   color: 'text-[#22d3ee]', bg: 'bg-[rgba(6,182,212,0.12)]',  border: 'border-[rgba(6,182,212,0.25)]',  dot: 'bg-[#22d3ee]' },
    completed: { label: 'Completed', color: 'text-[#34d399]', bg: 'bg-[rgba(16,185,129,0.12)]', border: 'border-[rgba(16,185,129,0.25)]', dot: 'bg-[#34d399]' },
    failed:    { label: 'Failed',    color: 'text-[#f87171]', bg: 'bg-[rgba(239,68,68,0.12)]',  border: 'border-[rgba(239,68,68,0.25)]',  dot: 'bg-[#f87171]' },
    queued:    { label: 'Queued',    color: 'text-[#94a3b8]', bg: 'bg-[rgba(100,116,139,0.12)]',border: 'border-[rgba(100,116,139,0.25)]',dot: 'bg-[#94a3b8]' },
    retrying:  { label: 'Retrying',  color: 'text-[#fbbf24]', bg: 'bg-[rgba(245,158,11,0.12)]', border: 'border-[rgba(245,158,11,0.25)]', dot: 'bg-[#fbbf24]' },
    paused:    { label: 'Paused',    color: 'text-[#a5b4fc]', bg: 'bg-[rgba(99,102,241,0.12)]', border: 'border-[rgba(99,102,241,0.25)]', dot: 'bg-[#a5b4fc]' },
  };
  return map[status];
}

function getSLAConfig(sla: SLAStatus) {
  const map: Record<SLAStatus, { label: string; color: string; bg: string; border: string }> = {
    'on-track': { label: 'On Track', color: 'text-[#34d399]', bg: 'bg-[rgba(16,185,129,0.1)]',  border: 'border-[rgba(16,185,129,0.2)]' },
    'at-risk':  { label: 'At Risk',  color: 'text-[#fbbf24]', bg: 'bg-[rgba(245,158,11,0.1)]',  border: 'border-[rgba(245,158,11,0.2)]' },
    'breached': { label: 'Breached', color: 'text-[#f87171]', bg: 'bg-[rgba(239,68,68,0.12)]',  border: 'border-[rgba(239,68,68,0.25)]' },
  };
  return map[sla];
}

function getPriorityConfig(p: Job['priority']) {
  const map = {
    critical: { label: 'Critical', color: 'text-[#fca5a5]' },
    high:     { label: 'High',     color: 'text-[#fbbf24]' },
    normal:   { label: 'Normal',   color: 'text-[#94a3b8]' },
    low:      { label: 'Low',      color: 'text-[#64748b]' },
  };
  return map[p];
}

function getCategoryIcon(cat: JobCategory) {
  if (cat === 'export') return Download;
  if (cat === 'webhook') return Webhook;
  return Layers;
}

function getCategoryColor(cat: JobCategory) {
  if (cat === 'export') return 'text-[#a5b4fc]';
  if (cat === 'webhook') return 'text-[#22d3ee]';
  return 'text-[#fbbf24]';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusDot({ status }: { status: JobStatus }) {
  const cfg = getStatusConfig(status);
  const pulse = status === 'running' || status === 'retrying';
  return (
    <span className="relative flex items-center justify-center w-2.5 h-2.5">
      {pulse && <span className={`absolute inline-flex h-full w-full rounded-full ${cfg.dot} opacity-40 animate-ping`} />}
      <span className={`relative inline-flex rounded-full w-2 h-2 ${cfg.dot}`} />
    </span>
  );
}

function StatusBadgeLocal({ status }: { status: JobStatus }) {
  const cfg = getStatusConfig(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold tracking-wide uppercase ${cfg.color} ${cfg.bg} border ${cfg.border}`}>
      <StatusDot status={status} />
      {cfg.label}
    </span>
  );
}

function SLABadge({ sla }: { sla: SLAStatus }) {
  const cfg = getSLAConfig(sla);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${cfg.color} ${cfg.bg} border ${cfg.border}`}>
      {sla === 'breached' ? <AlertTriangle size={9} /> : sla === 'at-risk' ? <AlertCircle size={9} /> : <CheckCircle size={9} />}
      {cfg.label}
    </span>
  );
}

function SLABar({ durationMs, slaMs, slaStatus }: { durationMs: number | null; slaMs: number; slaStatus: SLAStatus }) {
  const pct = getSLAPercent(durationMs, slaMs);
  const barColor = slaStatus === 'breached' ? 'bg-[#ef4444]' : slaStatus === 'at-risk' ? 'bg-[#f59e0b]' : 'bg-[#10b981]';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono-data text-[var(--muted-foreground)] w-8 text-right">{pct}%</span>
    </div>
  );
}

function ProgressBar({ progress }: { progress: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-[#06b6d4] transition-all duration-700" style={{ width: `${progress}%` }} />
      </div>
      <span className="text-[10px] font-mono-data text-[var(--muted-foreground)] w-7 text-right">{progress}%</span>
    </div>
  );
}

function RetryPips({ count, max }: { count: number; max: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <span
          key={i}
          className={`w-2 h-2 rounded-full ${i < count ? 'bg-[#f59e0b]' : 'bg-[var(--surface-4)]'}`}
        />
      ))}
      <span className="ml-1 text-[10px] font-mono-data text-[var(--muted-foreground)]">{count}/{max}</span>
    </div>
  );
}

// ─── Job Row ──────────────────────────────────────────────────────────────────

function JobRow({ job }: { job: Job }) {
  const [expanded, setExpanded] = useState(false);
  const CatIcon = getCategoryIcon(job.category);
  const catColor = getCategoryColor(job.category);
  const priority = getPriorityConfig(job.priority);

  return (
    <>
      <tr
        className="data-table-row cursor-pointer select-none"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Category */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <CatIcon size={14} className={catColor} />
            <span className={`text-[10px] font-semibold uppercase tracking-wider ${catColor}`}>
              {job.category === 'async' ? 'Async' : job.category === 'export' ? 'Export' : 'Webhook'}
            </span>
          </div>
        </td>
        {/* Job ID + Name */}
        <td className="px-4 py-3 max-w-[240px]">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-mono-data text-[var(--muted-foreground)]">{job.id}</span>
            <span className="text-sm font-medium text-[var(--foreground)] truncate">{job.name}</span>
          </div>
        </td>
        {/* Status */}
        <td className="px-4 py-3">
          <StatusBadgeLocal status={job.status} />
        </td>
        {/* Priority */}
        <td className="px-4 py-3">
          <span className={`text-[11px] font-semibold ${priority.color}`}>{priority.label}</span>
        </td>
        {/* Progress / Duration */}
        <td className="px-4 py-3 min-w-[140px]">
          {job.status === 'running' && job.progress !== null ? (
            <ProgressBar progress={job.progress} />
          ) : job.durationMs !== null ? (
            <span className="text-sm font-mono-data text-[var(--foreground)]">{formatDuration(job.durationMs)}</span>
          ) : (
            <span className="text-[var(--muted-foreground)] text-sm">—</span>
          )}
        </td>
        {/* SLA */}
        <td className="px-4 py-3 min-w-[160px]">
          <div className="flex flex-col gap-1">
            <SLABadge sla={job.slaStatus} />
            <SLABar durationMs={job.durationMs} slaMs={job.slaDeadlineMs} slaStatus={job.slaStatus} />
          </div>
        </td>
        {/* Retries */}
        <td className="px-4 py-3">
          <RetryPips count={job.retryCount} max={job.maxRetries} />
        </td>
        {/* Scheduled */}
        <td className="px-4 py-3">
          <span className="text-[11px] font-mono-data text-[var(--muted-foreground)]">
            {job.scheduledAt.split(' ')[1]}
          </span>
        </td>
        {/* Expand */}
        <td className="px-4 py-3 text-right">
          {expanded ? <ChevronUp size={14} className="text-[var(--muted-foreground)]" /> : <ChevronDown size={14} className="text-[var(--muted-foreground)]" />}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-[var(--surface-2)]">
          <td colSpan={9} className="px-6 py-4">
            <div className="grid grid-cols-2 gap-6 lg:grid-cols-3">
              {/* Left: Details */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Execution Details</p>
                <div className="space-y-2 text-sm">
                  {job.startedAt && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Started</span>
                      <span className="font-mono-data text-[var(--foreground)]">{job.startedAt}</span>
                    </div>
                  )}
                  {job.completedAt && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Completed</span>
                      <span className="font-mono-data text-[var(--foreground)]">{job.completedAt}</span>
                    </div>
                  )}
                  {job.nextRetryAt && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Next Retry</span>
                      <span className="font-mono-data text-[#fbbf24]">{job.nextRetryAt.split(' ')[1]}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">SLA Deadline</span>
                    <span className="font-mono-data text-[var(--foreground)]">{formatDuration(job.slaDeadlineMs)}</span>
                  </div>
                  {job.durationMs !== null && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Elapsed</span>
                      <span className="font-mono-data text-[var(--foreground)]">{formatDuration(job.durationMs)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Middle: Job-specific */}
              <div className="space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
                  {job.category === 'webhook' ? 'Delivery Info' : 'Job Info'}
                </p>
                <div className="space-y-2 text-sm">
                  {job.endpoint && (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[var(--muted-foreground)]">Endpoint</span>
                      <span className="font-mono-data text-[11px] text-[#22d3ee] break-all">{job.endpoint}</span>
                    </div>
                  )}
                  {job.httpStatus !== undefined && job.httpStatus !== null && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">HTTP Status</span>
                      <span className={`font-mono-data font-bold ${job.httpStatus >= 200 && job.httpStatus < 300 ? 'text-[#34d399]' : 'text-[#f87171]'}`}>
                        {job.httpStatus}
                      </span>
                    </div>
                  )}
                  {job.format && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Format</span>
                      <span className="font-mono-data text-[var(--foreground)]">{job.format}</span>
                    </div>
                  )}
                  {job.recipients !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Recipients</span>
                      <span className="font-mono-data text-[var(--foreground)]">{job.recipients}</span>
                    </div>
                  )}
                  {job.recordCount !== undefined && job.recordCount !== null && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Records</span>
                      <span className="font-mono-data text-[var(--foreground)]">{job.recordCount.toLocaleString()}</span>
                    </div>
                  )}
                  {job.throughput && (
                    <div className="flex justify-between">
                      <span className="text-[var(--muted-foreground)]">Throughput</span>
                      <span className="font-mono-data text-[#34d399]">{job.throughput}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Error / Actions */}
              <div className="space-y-3">
                {job.errorMessage && (
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#f87171]">Error</p>
                    <div className="p-3 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)]">
                      <p className="text-[12px] font-mono-data text-[#fca5a5] leading-relaxed">{job.errorMessage}</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {(job.status === 'failed') && (
                    <button className="btn-secondary text-xs py-1.5 px-3 gap-1.5">
                      <RotateCcw size={12} /> Retry Now
                    </button>
                  )}
                  {(job.status === 'running' || job.status === 'queued') && (
                    <button className="btn-secondary text-xs py-1.5 px-3 gap-1.5">
                      <Pause size={12} /> Pause
                    </button>
                  )}
                  {job.status === 'paused' && (
                    <button className="btn-secondary text-xs py-1.5 px-3 gap-1.5">
                      <Play size={12} /> Resume
                    </button>
                  )}
                  <button className="btn-ghost text-xs py-1.5 px-3 gap-1.5">
                    <Eye size={12} /> View Logs
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── SLA Panel ────────────────────────────────────────────────────────────────

function SLAPanel({ rules }: { rules: SLARule[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {rules.map(rule => {
        const CatIcon = getCategoryIcon(rule.category);
        const catColor = getCategoryColor(rule.category);
        const p95Pct = Math.min(100, Math.round((rule.currentP95 / rule.slaMs) * 100));
        const p50Pct = Math.min(100, Math.round((rule.currentP50 / rule.slaMs) * 100));
        const breachRate = rule.totalJobs > 0 ? ((rule.breachCount / rule.totalJobs) * 100).toFixed(1) : '0.0';
        const slaOk = rule.breachCount === 0;

        return (
          <div key={rule.category} className="glass-card rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CatIcon size={16} className={catColor} />
                <span className="text-sm font-semibold text-[var(--foreground)]">{rule.label}</span>
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${slaOk ? 'text-[#34d399] bg-[rgba(16,185,129,0.1)] border-[rgba(16,185,129,0.2)]' : 'text-[#f87171] bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.2)]'}`}>
                {slaOk ? 'Healthy' : `${rule.breachCount} Breach${rule.breachCount > 1 ? 'es' : ''}`}
              </span>
            </div>

            {/* P50 bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-[11px]">
                <span className="text-[var(--muted-foreground)]">P50 latency</span>
                <span className="font-mono-data text-[var(--foreground)]">{formatDuration(rule.currentP50)}</span>
              </div>
              <div className="h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-[#10b981]" style={{ width: `${p50Pct}%` }} />
              </div>
            </div>

            {/* P95 bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-[11px]">
                <span className="text-[var(--muted-foreground)]">P95 latency</span>
                <span className={`font-mono-data ${p95Pct > 80 ? 'text-[#f59e0b]' : 'text-[var(--foreground)]'}`}>{formatDuration(rule.currentP95)}</span>
              </div>
              <div className="h-1.5 bg-[var(--surface-4)] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${p95Pct > 80 ? 'bg-[#f59e0b]' : 'bg-[#06b6d4]'}`} style={{ width: `${p95Pct}%` }} />
              </div>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-[var(--border)]">
              <div className="text-center">
                <p className="text-[10px] text-[var(--muted-foreground)]">SLA Target</p>
                <p className="text-sm font-mono-data font-bold text-[var(--foreground)]">{formatDuration(rule.slaMs)}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-[var(--muted-foreground)]">Breach Rate</p>
                <p className={`text-sm font-mono-data font-bold ${parseFloat(breachRate) > 0 ? 'text-[#f87171]' : 'text-[#34d399]'}`}>{breachRate}%</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] text-[var(--muted-foreground)]">Total Jobs</p>
                <p className="text-sm font-mono-data font-bold text-[var(--foreground)]">{rule.totalJobs}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function JobMonitorDashboard() {
  const [jobs, setJobs] = useState<Job[]>(initialJobs);
  const [activeTab, setActiveTab] = useState<'all' | JobCategory>('all');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [lastRefresh, setLastRefresh] = useState<string>('03:42:46');
  const [liveMode, setLiveMode] = useState(true);
  const [tick, setTick] = useState(0);

  // Simulate live updates
  useEffect(() => {
    if (!liveMode) return;
    const interval = setInterval(() => {
      setTick(t => t + 1);
      setLastRefresh(new Date().toLocaleTimeString('en-GB', { hour12: false }));
      setJobs(prev => prev.map(job => {
        if (job.status === 'running' && job.progress !== null) {
          const newProgress = Math.min(100, job.progress + Math.floor(Math.random() * 3) + 1);
          return { ...job, progress: newProgress, status: newProgress >= 100 ? 'completed' : 'running' };
        }
        return job;
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, [liveMode]);

  const filtered = jobs.filter(j => {
    const catMatch = activeTab === 'all' || j.category === activeTab;
    const statusMatch = statusFilter === 'all' || j.status === statusFilter;
    const searchMatch = !search || j.name.toLowerCase().includes(search.toLowerCase()) || j.id.toLowerCase().includes(search.toLowerCase());
    return catMatch && statusMatch && searchMatch;
  });

  const counts = {
    running:   jobs.filter(j => j.status === 'running').length,
    failed:    jobs.filter(j => j.status === 'failed').length,
    retrying:  jobs.filter(j => j.status === 'retrying').length,
    queued:    jobs.filter(j => j.status === 'queued').length,
    breached:  jobs.filter(j => j.slaStatus === 'breached').length,
    atRisk:    jobs.filter(j => j.slaStatus === 'at-risk').length,
  };

  const tabs: { key: 'all' | JobCategory; label: string; icon: React.ElementType; count: number }[] = [
    { key: 'all',     label: 'All Jobs',    icon: Layers,    count: jobs.length },
    { key: 'export',  label: 'Exports',     icon: Download,  count: jobs.filter(j => j.category === 'export').length },
    { key: 'webhook', label: 'Webhooks',    icon: Webhook,   count: jobs.filter(j => j.category === 'webhook').length },
    { key: 'async',   label: 'Async Queue', icon: Layers,    count: jobs.filter(j => j.category === 'async').length },
  ];

  const statusFilters: { key: JobStatus | 'all'; label: string }[] = [
    { key: 'all',       label: 'All' },
    { key: 'running',   label: 'Running' },
    { key: 'queued',    label: 'Queued' },
    { key: 'retrying',  label: 'Retrying' },
    { key: 'failed',    label: 'Failed' },
    { key: 'completed', label: 'Completed' },
  ];

  return (
    <div className="flex flex-col gap-6 p-6 min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={20} className="text-[#10b981]" />
            <h1 className="text-xl font-bold text-[var(--foreground)]">Job Monitor</h1>
            <span className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${liveMode ? 'text-[#34d399] bg-[rgba(16,185,129,0.1)] border-[rgba(16,185,129,0.2)]' : 'text-[#94a3b8] bg-[rgba(100,116,139,0.1)] border-[rgba(100,116,139,0.2)]'}`}>
              {liveMode ? <><span className="w-1.5 h-1.5 rounded-full bg-[#34d399] animate-pulse" />Live</> : 'Paused'}
            </span>
          </div>
          <p className="text-sm text-[var(--muted-foreground)]">
            Real-time visibility into scheduled exports, webhook deliveries, and async job queue — with SLA tracking and retry status.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] font-mono-data text-[var(--muted-foreground)]">Last refresh: {lastRefresh}</span>
          <button
            onClick={() => setLiveMode(l => !l)}
            className={`btn-secondary text-xs py-1.5 px-3 gap-1.5 ${liveMode ? 'border-[rgba(16,185,129,0.3)] text-[#34d399]' : ''}`}
          >
            {liveMode ? <><WifiOff size={12} /> Pause</> : <><Wifi size={12} /> Go Live</>}
          </button>
          <button className="btn-secondary text-xs py-1.5 px-3 gap-1.5" onClick={() => setLastRefresh(new Date().toLocaleTimeString('en-GB', { hour12: false }))}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <div className="metric-info rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <Activity size={12} className="text-[#22d3ee]" /> Running
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#22d3ee]">{counts.running}</p>
        </div>
        <div className="metric-warning rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <Clock size={12} className="text-[#fbbf24]" /> Queued
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#fbbf24]">{counts.queued}</p>
        </div>
        <div className="metric-warning rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <RotateCcw size={12} className="text-[#fbbf24]" /> Retrying
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#fbbf24]">{counts.retrying}</p>
        </div>
        <div className="metric-negative rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <XCircle size={12} className="text-[#f87171]" /> Failed
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#f87171]">{counts.failed}</p>
        </div>
        <div className="metric-critical rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <AlertTriangle size={12} className="text-[#fca5a5]" /> SLA Breached
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#fca5a5]">{counts.breached}</p>
        </div>
        <div className="metric-warning rounded-xl p-4 flex flex-col gap-1">
          <div className="flex items-center gap-1.5 text-[11px] text-[var(--muted-foreground)] uppercase tracking-wider font-semibold">
            <AlertCircle size={12} className="text-[#fbbf24]" /> SLA At Risk
          </div>
          <p className="text-2xl font-bold font-mono-data text-[#fbbf24]">{counts.atRisk}</p>
        </div>
      </div>

      {/* SLA Tracking */}
      <div className="space-y-3">
        <SectionHeader title="Execution SLA Tracking" subtitle="P50/P95 latency vs. SLA targets per job category" />
        <SLAPanel rules={slaRules} />
      </div>

      {/* Job Table */}
      <div className="space-y-3">
        <SectionHeader title="Job Queue" subtitle="Click any row to expand execution details, retry controls, and error diagnostics" />

        {/* Tabs + Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Category tabs */}
          <div className="flex items-center gap-1 p-1 bg-[var(--surface-2)] rounded-lg border border-[var(--border)]">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${active ? 'bg-[var(--surface-4)] text-[var(--foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}
                >
                  <TabIcon size={12} />
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[rgba(16,185,129,0.2)] text-[#34d399]' : 'bg-[var(--surface-3)] text-[var(--muted-foreground)]'}`}>
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Status filter + Search */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <input
                type="text"
                placeholder="Search jobs…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-[var(--surface-2)] border border-[var(--border)] rounded-lg text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)] w-44"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as JobStatus | 'all')}
              className="text-xs bg-[var(--surface-2)] border border-[var(--border)] rounded-lg px-2.5 py-1.5 text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]"
            >
              {statusFilters.map(f => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--surface-2)]">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Type</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Job</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Status</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Priority</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Progress / Duration</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">SLA</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Retries</th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">Scheduled</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-[var(--muted-foreground)] text-sm">
                      No jobs match the current filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map(job => <JobRow key={job.id} job={job} />)
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-[var(--border)] flex items-center justify-between">
            <span className="text-[11px] text-[var(--muted-foreground)]">
              Showing {filtered.length} of {jobs.length} jobs
            </span>
            <div className="flex items-center gap-3 text-[11px] text-[var(--muted-foreground)]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#22d3ee] animate-ping opacity-60" />Live updates every 3s</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
